<template>
  <div class="row col-12">
    <div
      ref="commentsObserverContainer"
      class="full-width"
    >
      <q-spinner v-if="loading" />
      <q-chat-message
        v-for="chatMessage in chatMessages"
        :key="chatMessage.index"
        :name="chatMessage.name"
        :bg-color="chatMessage.color"
        :sent="chatMessage.sent"
        :stamp="chatMessage.stamp"
        class="session-comment"
        :class="{ 'cursor-pointer': chatMessage.clickable }"
        @click="onChatMessageClick(chatMessage)"
      >
        <div
          v-for="(text, idx) in chatMessage.texts"
          :key="text.id"
          ref="commentObservableItem"
          :data-msgid="text.id"
          class="message-text-wrapper cursor-pointer"
          @click="onTextClick(text.id, chatMessage)"
        >
          <div
            v-if="selectedMessageId === text.id"
            class="absolute delete-message-btn"
          >
            <div v-if="!text.resolved && (text.type !== 'signatureRequest')">
              <q-btn
                color="primary"
                round
                dense
                size="sm"
                icon="check"
                flat
                @click="onMessageResolve(text.id)"
              />
            </div>
            <div
              v-if="chatMessage.author === userDetails.userId"
            >
              <q-btn
                color="red"
                round
                dense
                size="sm"
                icon="delete"
                flat
                @click="onMessageDelete(text.id)"
              />
            </div>
          </div>
          <q-icon
            v-if="hasMoodReaction && (idx === 0)"
            :name="moodReactionIcon"
            color="primary"
            size="sm"
            class="q-ma-sm"
          >
            <q-tooltip>
              {{ moodReactionTooltip }}
            </q-tooltip>
          </q-icon>
          <span v-html="text.message" />
          <div
            v-if="text.resolved"
            class="flex justify-end"
          >
            <span
              class="text-caption"
            >
              {{ $t('signature.textResolved') }} <q-icon
                size="xs"
                name="done_all"
              />
            </span>
          </div>

          <div
            v-if="(text.type !== 'signatureRequest') && (chatMessage.author === userDetails.userId)"
            class="file-overlay"
            :class="{ darkened: selectedMessageId === text.id }"
          />

          <q-menu
            v-if="chatMessage.author === userDetails.userId"
            touch-position
            context-menu
          >
            <q-list dense>
              <q-item
                v-close-popup
                clickable
              >
                <q-item-section
                  @click="onMessageDelete(text.id)"
                >
                  {{ $t('common.buttonDelete') }}
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </div>
      </q-chat-message>
    </div>
    <div
      v-if="!readOnly && !preventComment"
      class="row col-12 items-center justify-between"
    >
      <tiptap-input
        v-model="commentInput"
        :session-id="origin === 'session' ? originId : null"
        :client-id="client"
        :visibility-settings="visibilitySettings"
        :class="{
          'col': !stack,
          'col-12': stack,
        }"
        @submit="onAddComment"
      >
        <template
          v-if="currentTimestamp !== null"
          #prepend
        >
          <div
            class="text-grey-7 text-caption"
          >
            {{ currentTimestampFormatted }}
          </div>
        </template>
      </tiptap-input>
      <div
        v-if="!stack"
        class="q-pl-sm"
      >
        <q-btn
          icon="send"
          color="positive"
          size="sm"
          round
          :disable="!commentInputTrimmed"
          :loading="isSendingMessage"
          @click="onAddComment"
        >
          <q-tooltip>
            {{ $t('sessions.sessionCard.buttonSendComment') }}
          </q-tooltip>
        </q-btn>
      </div>
      <div
        v-if="addCommentHint"
        :class="{
          'col-12': !stack,
          'col': stack,
        }"
        class="q-field__bottom"
      >
        {{ addCommentHint }}
      </div>
      <div
        v-if="stack"
        class="col-auto q-py-xs q-pl-xs"
      >
        <q-btn
          icon="send"
          color="positive"
          size="sm"
          :label=" $t('common.buttonSend') "
          :disable="!commentInputTrimmed"
          :loading="isSendingMessage"
          @click="onAddComment"
        >
          <q-tooltip>
            {{ $t('sessions.sessionCard.buttonSendComment') }}
          </q-tooltip>
        </q-btn>
      </div>
    </div>
  </div>
</template>
<script>
import 'intersection-observer';

import TiptapInput from 'components/UI/TiptapInput';
import { arrayUnion } from 'firebase/firestore';
import { chain, concat, find, get, isEmpty } from 'lodash';
import { mapState } from 'pinia';
import MessagesDB from 'src/fb/messages-db';
import MoodReactions from 'src/mixins/MoodReactions';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { logEvent } from 'src/utils/LogEvent';

export default {
  name: 'MessageThread',

  components: {
    TiptapInput,
  },

  mixins: [MoodReactions, Toolbox],

  props: {
    messages: {
      type: Array,
      required: true,
    },

    thread: {
      type: String,
      required: true,
    },

    origin: {
      type: String,
      required: true,
    },

    originId: {
      type: String,
      required: true,
    },

    client: {
      type: String,
      default: null,
      required: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    readOnly: {
      type: Boolean,
      default: false,
    },

    preventComment: {
      type: Boolean,
      default: false,
    },

    currentTimestamp: {
      type: Number,
      default: null,
    },

    moodReaction: {
      type: String,
      default: null,
    },

    addCommentHint: {
      type: String,
      default: null,
    },

    visibilitySettings: {
      type: Object,
      default: () => ({}),
    },

    stack: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['click', 'message-read'],

  data() {
    return {
      isSendingMessage: false,
      selectedMessageId: '',
      commentInput: {
        text: '',
        html: '',
      },
      observer: null,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),

    currentTimestampFormatted() {
      if (this.currentTimestamp === null) {
        return '';
      }
      return this.formatDuration(this.currentTimestamp);
    },

    hasMoodReaction() {
      return !!this.moodReaction;
    },

    moodReactionIcon() {
      return chain(this.reactionOptions).find({ value: this.moodReaction }).get('icon').value();
    }, // moodReactionIcon()

    moodReactionTooltip() {
      return chain(this.reactionOptions).find({ value: this.moodReaction }).get('tooltip').value();
    }, // moodReactionTooltip()

    commentInputTrimmed() {
      return this.commentInput.text.trim();
    }, // commentInputTrimmed()

    chatMessages() {
      return chain(this.messages)
        .reduce((pV, comment, idx) => {
          let currentMessage = chain(pV).last().value();

          if (!currentMessage
            || get(currentMessage, 'author', null) !== comment.author
            || get(currentMessage, 'sessionTimeOffset', null) !== comment.sessionTimeOffset
          ) {
            currentMessage = {
              index: idx,
              author: comment.author,
              name: comment.author === this.userDetails.userId ? '' : comment.authorName,
              color: comment.author === this.userDetails.userId ? '' : 'primary-light',
              texts: [{
                message: comment.messageHtml || comment.message,
                id: comment.id,
                type: comment.type,
                requestedUser: comment.requestedUser,
                signatoryId: comment.signatoryId || null,
                resolved: comment.resolved || false,
              }],
              sent: comment.author === this.userDetails.userId,
              clickable: !!comment.sessionTimeOffset,
              stamp: comment.sessionTimeOffset ? this.formatDuration(comment.sessionTimeOffset) : '',
              sessionTimeOffset: comment.sessionTimeOffset,
            };
            return concat(pV, currentMessage);
          }

          currentMessage.texts.push({
            message: comment.messageHtml || comment.message,
            id: comment.id,
            type: comment.type,
            requestedUser: comment.requestedUser,
            resolved: comment.resolved || false,
          });

          return concat(pV.slice(0, -1), currentMessage);
        }, [])
        .value();
    }, // chatMessages()

  }, // computed

  watch: {

    chatMessages: {
      deep: true,
      immediate: true,
      handler(newData) {
        this.$nextTick(() => {
          if (!isEmpty(newData) && this.$refs.commentObservableItem) {
            this.$refs.commentObservableItem.forEach((el) => {
              this.observer.observe(el);
            });
          }
        });
      },
    },
  },

  async created() {
    this.observer = new IntersectionObserver(this.onCommentObserved, {
      root: this.$refs.commentsObserverContainer,
      threshold: 1.0,
    });
  },

  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },

  methods: {

    onCommentObserved(entry) {
      entry.forEach(({ target, isIntersecting }) => {
        if (!isIntersecting) {
          return;
        }
        this.observer.unobserve(target);
        const message = find(this.messages, { id: target.dataset.msgid }) || null;
        if (!message) {
          return;
        }
        if (message.author !== this.userDetails.userId && !message.readBy?.includes(this.userDetails.userId)) {
          // Mark message as read
          const messagesDb = new MessagesDB();
          try {
            messagesDb.set({ readBy: arrayUnion(this.userDetails.userId) }, message.id);
          } catch (err) {
            logEvent(err);
            console.error('Error marking message as read', err);
          }
        }

        this.$emit('message-read', message);
      });
    }, // onCommentObserved()

    onChatMessageClick(chatMessage) {
      if (chatMessage.clickable) {
        this.$emit('click', chatMessage);
      }
    },

    onTextClick(messageId) {
      if (!messageId) {
        this.selectedMessageId = '';
      } else {
        this.selectedMessageId = this.selectedMessageId === messageId ? '' : messageId;
      }
    }, // onTextClick()

    async onAddComment() {
      if (!this.commentInputTrimmed || this.isSendingMessage) {
        return;
      }

      const [, threadSubPath] = this.thread.split('_');

      const messageObj = {
        author: this.userDetails.userId,
        authorName: this.userDetails.name,
        thread: this.thread,
        threadSubPath,
        [this.origin]: this.originId,
        sessionTimeOffset: this.currentTimestamp,
        message: this.commentInputTrimmed,
        messageHtml: this.commentInput.html,
        clinic: this.userDetails.clinic,
        client: this.client,
        readBy: [this.userDetails.userId],
      };

      const messagesDb = new MessagesDB();
      this.isSendingMessage = true;
      try {
        await messagesDb.create(messageObj);
      } finally {
        this.isSendingMessage = false;
        this.commentInput = {
          text: '',
          html: '',
        };
      }
    }, // onAddComment()

    onMessageResolve(messageId) {
      const messagesDb = new MessagesDB();
      messagesDb.set({ resolved: true }, messageId);
    }, // onMessageResolve()

    onMessageDelete(messageId) {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('library.modals.deleteMessage.textCaption'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          const messagesDb = new MessagesDB();
          await messagesDb.delete(messageId);
        });
    }, // onMessageDelete()

  },
};
</script>
<style lang="scss" scoped>

.session-comment {
  :deep(.q-message-text) {
    padding: 0;
  }

  :deep(.q-message-stamp) {
    padding: 0 8px 8px 8px;
  }

  // Keeps room for .delete-message-btn, which sits outside the bubble, so a long comment can't push it off-screen.
  &.q-message-sent :deep(.q-message-container) {
    padding-inline-start: 35px;
  }
  &.q-message-received :deep(.q-message-container) {
    padding-inline-end: 35px;
  }

  .q-message-text {
    .message-text-wrapper {
      padding: 8px;
    }

    .file-overlay {
      opacity: 1;

      &:hover {
        background-color: rgba(0, 0, 0, .2);
      }
    }

    .darkened {
      background-color: rgba(0, 0, 0, .2);
    }

    .delete-message-btn {
      top: 0;
    }

    &:hover {
      .delete-message-btn {
        display: inline;
      }
    }

    &--received .delete-message-btn {
      right: -35px;
    }
    &--sent .delete-message-btn {
      left: -35px;
    }
  }
}

</style>
